import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  AlertSettingsCard,
  AlertProfilesList,
  AlertProfileEditor,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Spinner,
} from '@roselabs-io/saas-shell'
import type {
  AlertProfile,
  Integration,
  CreateAlertProfileRequest,
  UpdateAlertProfileRequest,
} from '@roselabs-io/saas-shell'
import { notificationsApi } from '../api/notifications'
import { PageHeader } from '../components'
import { Bell } from 'lucide-react'
import toast from 'react-hot-toast'

export default function IntegrationsPage() {
  const { teamId } = useParams<{ teamId: string }>()

  // Alert profiles state
  const [profiles, setProfiles] = useState<AlertProfile[]>([])
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true)
  const [selectedProfile, setSelectedProfile] = useState<AlertProfile | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)

  // Load alert profiles and integrations
  const loadData = useCallback(async () => {
    if (!teamId) return
    setIsLoadingProfiles(true)
    try {
      const [profilesData, integrationsData] = await Promise.all([
        notificationsApi.listAlertProfiles(teamId),
        notificationsApi.listIntegrations(teamId),
      ])
      setProfiles(profilesData)
      setIntegrations(integrationsData)
    } catch (err) {
      console.error('Failed to load alert profiles:', err)
      toast.error('Failed to load alert profiles')
    } finally {
      setIsLoadingProfiles(false)
    }
  }, [teamId])

  useEffect(() => {
    loadData()
  }, [loadData])

  if (!teamId) {
    return null
  }

  const handleSelectProfile = (profile: AlertProfile) => {
    setSelectedProfile(profile)
    setIsEditorOpen(true)
  }

  const handleCreateNew = () => {
    setSelectedProfile(null)
    setIsEditorOpen(true)
  }

  const handleSaveProfile = async (data: CreateAlertProfileRequest | UpdateAlertProfileRequest) => {
    if (selectedProfile) {
      // Update existing
      const updated = await notificationsApi.updateAlertProfile(
        teamId,
        selectedProfile.id,
        data as UpdateAlertProfileRequest
      )
      setProfiles(prev => prev.map(p => (p.id === updated.id ? updated : p)))
      toast.success('Alert profile updated')
    } else {
      // Create new
      const created = await notificationsApi.createAlertProfile(
        teamId,
        data as CreateAlertProfileRequest
      )
      setProfiles(prev => [...prev, created])
      toast.success('Alert profile created')
    }
  }

  const handleDeleteProfile = async (profileId: string) => {
    await notificationsApi.deleteAlertProfile(teamId, profileId)
    setProfiles(prev => prev.filter(p => p.id !== profileId))
    toast.success('Alert profile deleted')
  }

  const handleSetDefault = async (profileId: string) => {
    const updated = await notificationsApi.setDefaultAlertProfile(teamId, profileId)
    // Update local state - set all to non-default, then set this one to default
    setProfiles(prev =>
      prev.map(p => ({
        ...p,
        is_default: p.id === updated.id,
      }))
    )
    toast.success('Default alert profile updated')
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <PageHeader
        title="Alert Settings"
        subtitle="Configure how you receive alerts and notifications."
      />

      {/* Alert Profiles Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-primary-600" />
            <div>
              <CardTitle>Alert Profiles</CardTitle>
              <CardDescription>
                Create reusable alert configurations with severity filtering, quiet hours,
                thresholds, and escalation rules. Assign profiles to apps in their settings.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {isLoadingProfiles ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <AlertProfilesList
              profiles={profiles}
              onSelect={handleSelectProfile}
              onCreateNew={handleCreateNew}
              onDelete={handleDeleteProfile}
              onSetDefault={handleSetDefault}
            />
          )}
        </CardBody>
      </Card>

      {/* Integrations, Recipients, Preferences */}
      <AlertSettingsCard
        teamId={teamId}
        productSlug="falcon"
        notificationsApi={notificationsApi}
        showQuietHours={false}
        title="Notification Channels"
        description="Configure integrations, email recipients, and alert preferences."
      />

      {/* Alert Profile Editor Modal */}
      <AlertProfileEditor
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        profile={selectedProfile}
        integrations={integrations}
        onSave={handleSaveProfile}
      />
    </div>
  )
}
